import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, Eye, EyeOff, ArrowUp, ArrowDown, Menu } from "lucide-react";

const emptyItem = {
  label: "", path: "/", parent_id: null, display_order: 100,
  is_visible: true, open_in_new_tab: false,
};

export default function AdminNavigation() {
  const { authedClient } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyItem);
  const [pages, setPages] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, p] = await Promise.all([
        authedClient().get("/admin/navigation"),
        authedClient().get("/admin/pages"),
      ]);
      setItems(n.data);
      setPages(p.data);
    } catch { toast.error("Could not load navigation"); } finally { setLoading(false); }
  }, [authedClient]);

  useEffect(() => { load(); }, [load]);

  const tops = items.filter(i => !i.parent_id).sort((a, b) => a.display_order - b.display_order);
  const childrenOf = (id) => items.filter(i => i.parent_id === id).sort((a, b) => a.display_order - b.display_order);

  const save = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error("Label is required");
    if (!form.path.trim()) return toast.error("Path is required");
    try {
      if (editing === "new") {
        const { data } = await authedClient().post("/admin/navigation", form);
        setItems([...items, data]);
        toast.success("Nav item added");
      } else {
        const { data } = await authedClient().patch(`/admin/navigation/${editing}`, form);
        setItems(items.map(x => x.id === editing ? data : x));
        toast.success("Saved");
      }
      setEditing(null); setForm(emptyItem);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const toggle = async (item) => {
    try {
      const { data } = await authedClient().patch(`/admin/navigation/${item.id}`, { is_visible: !item.is_visible });
      setItems(items.map(x => x.id === item.id ? data : x));
    } catch { toast.error("Could not toggle"); }
  };
  const move = async (item, dir) => {
    const delta = dir === "up" ? -15 : 15;
    try {
      const { data } = await authedClient().patch(`/admin/navigation/${item.id}`, { display_order: item.display_order + delta });
      setItems(items.map(x => x.id === item.id ? data : x));
    } catch { toast.error("Could not reorder"); }
  };
  const remove = async (item) => {
    if (item.is_system) return toast.error("System items can't be deleted - hide or rename instead");
    if (!window.confirm(`Delete "${item.label}"?`)) return;
    try {
      await authedClient().delete(`/admin/navigation/${item.id}`);
      setItems(items.filter(x => x.id !== item.id && x.parent_id !== item.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-navigation-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="eyebrow mb-2">★ CMS</p>
          <h1 className="font-display text-3xl text-[color:var(--blue-ink)]">Navigation Menu</h1>
          <p className="text-sm text-[color:var(--ink-soft)] mt-1">
            Rename any tab, hide items you don&rsquo;t want visible, reorder with the arrows, or add
            brand-new links. Custom pages you create in <strong>Pages</strong> can be linked here.
          </p>
        </div>
        {!editing && (
          <button onClick={() => { setForm(emptyItem); setEditing("new"); }} className="btn-primary" data-testid="admin-nav-new">
            <Plus className="w-4 h-4" /> Add nav item
          </button>
        )}
      </div>

      {editing && (
        <form onSubmit={save} className="bg-[color:var(--cream)] rounded-2xl p-6 mb-8 space-y-4" data-testid="admin-nav-form">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl text-[color:var(--blue-ink)]">{editing === "new" ? "Add nav item" : "Edit nav item"}</h2>
            <button type="button" onClick={() => { setEditing(null); setForm(emptyItem); }}><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Label (shown in menu) *">
              <input required value={form.label} onChange={e => setForm({...form, label: e.target.value})} className={ic} data-testid="nav-form-label" />
            </Field>
            <Field label="Path or URL *">
              <input required value={form.path} onChange={e => setForm({...form, path: e.target.value})} className={ic} placeholder="/about or /pages/custom or https://…" data-testid="nav-form-path" />
              <p className="text-xs text-[color:var(--muted)] mt-1">
                Use <code>/pages/&lt;slug&gt;</code> for a custom CMS page, a site path like <code>/about</code>,
                or a full <code>https://</code> URL for external links.
              </p>
              {pages.length > 0 && (
                <div className="mt-2 text-xs">
                  <span className="text-[color:var(--muted)]">Quick pick a custom page: </span>
                  {pages.map(p => (
                    <button key={p.id} type="button" onClick={() => setForm(f => ({...f, path: `/pages/${p.slug}`}))}
                            className="inline-block ml-1.5 px-2 py-0.5 rounded bg-[color:var(--blue)]/10 text-[color:var(--blue-deep)] hover:bg-[color:var(--blue)]/20">
                      /pages/{p.slug}
                    </button>
                  ))}
                </div>
              )}
            </Field>
            <Field label="Parent (leave blank for top-level)">
              <select value={form.parent_id || ""} onChange={e => setForm({...form, parent_id: e.target.value || null})} className={ic}>
                <option value="">- Top-level (main nav) -</option>
                {tops.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Display order (smaller shows first)">
              <input type="number" value={form.display_order} onChange={e => setForm({...form, display_order: parseInt(e.target.value || "0", 10)})} className={ic} />
            </Field>
          </div>
          <div className="flex flex-wrap gap-5">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_visible} onChange={e => setForm({...form, is_visible: e.target.checked})} className="accent-[color:var(--blue)]" />
              Visible in navigation
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.open_in_new_tab} onChange={e => setForm({...form, open_in_new_tab: e.target.checked})} className="accent-[color:var(--blue)]" />
              Open in new tab
            </label>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" data-testid="nav-form-save"><Save className="w-4 h-4" /> Save</button>
            <button type="button" onClick={() => { setEditing(null); setForm(emptyItem); }} className="btn-ghost">Cancel</button>
          </div>
        </form>
      )}

      {loading ? <p className="text-center text-[color:var(--muted)] py-8">Loading…</p> : (
        <div className="space-y-3">
          {tops.map(top => (
            <div key={top.id} className="p-4 rounded-xl border border-[color:var(--line)] bg-white" data-testid={`nav-top-${top.id}`}>
              <Row item={top} onEdit={() => { setForm({...emptyItem, ...top}); setEditing(top.id); }}
                   onToggle={() => toggle(top)} onDelete={() => remove(top)}
                   onUp={() => move(top, "up")} onDown={() => move(top, "down")} />
              {childrenOf(top.id).length > 0 && (
                <div className="mt-3 ml-6 pl-4 border-l-2 border-[color:var(--line)] space-y-2">
                  {childrenOf(top.id).map(c => (
                    <Row key={c.id} item={c} nested onEdit={() => { setForm({...emptyItem, ...c}); setEditing(c.id); }}
                         onToggle={() => toggle(c)} onDelete={() => remove(c)}
                         onUp={() => move(c, "up")} onDown={() => move(c, "down")} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ item, nested, onEdit, onToggle, onDelete, onUp, onDown }) {
  return (
    <div className={`flex items-center gap-3 ${nested ? "text-sm" : ""}`} data-testid={`nav-row-${item.id}`}>
      <Menu className="w-4 h-4 text-[color:var(--muted)] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className={`font-medium truncate ${item.is_visible ? "text-[color:var(--blue-ink)]" : "text-[color:var(--muted)] line-through"}`}>{item.label}</div>
        <div className="text-xs text-[color:var(--muted)] truncate">{item.path} · order {item.display_order}{item.is_system && " · system"}</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={onUp} className="p-1 rounded hover:bg-[color:var(--cream)]" title="Move up"><ArrowUp className="w-4 h-4" /></button>
        <button onClick={onDown} className="p-1 rounded hover:bg-[color:var(--cream)]" title="Move down"><ArrowDown className="w-4 h-4" /></button>
        <button onClick={onToggle} className="p-1 rounded hover:bg-[color:var(--cream)]" title={item.is_visible ? "Hide" : "Show"}>
          {item.is_visible ? <Eye className="w-4 h-4 text-[color:var(--blue-deep)]" /> : <EyeOff className="w-4 h-4 text-[color:var(--muted)]" />}
        </button>
        <button onClick={onEdit} className="p-1 rounded hover:bg-[color:var(--cream)]" title="Edit"><Pencil className="w-4 h-4" /></button>
        <button onClick={onDelete} disabled={item.is_system} className="p-1 rounded text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed" title={item.is_system ? "System items cannot be deleted" : "Delete"}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

const ic = "w-full rounded-lg border border-[color:var(--line)] px-3 py-2 bg-white focus:outline-none focus:border-[color:var(--blue)]";
function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-[color:var(--ink-soft)] mb-1">{label}</label>{children}</div>;
}
